/**
 * AGI Playground View
 * Streaming AGI interactions with code execution and multi-modal support
 */

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Image as ImageIcon,
  Paperclip,
  Code,
  Sparkles,
  Copy,
  Check,
  Trash2,
  Play,
  StopCircle,
  RefreshCw,
  MoreVertical,
  Terminal,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";

interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
  isStreaming?: boolean;
  attachments?: Attachment[];
  metadata?: {
    model?: string;
    tokens?: number;
    duration?: number;
  };
}

interface Attachment {
  id: string;
  type: "image" | "file" | "code";
  name: string;
  content: string;
  preview?: string;
}

interface ModelOption {
  id: string;
  name: string;
  provider: "openai" | "anthropic" | "local" | "custom";
  type: "chat" | "code" | "multimodal";
  capabilities: string[];
}

const MODELS: ModelOption[] = [
  { id: "gpt-4o", name: "GPT-4o", provider: "openai", type: "multimodal", capabilities: ["text", "image", "code", "tools"] },
  { id: "gpt-4o-mini", name: "GPT-4o Mini", provider: "openai", type: "multimodal", capabilities: ["text", "image", "code"] },
  { id: "claude-3-5-sonnet", name: "Claude 3.5 Sonnet", provider: "anthropic", type: "chat", capabilities: ["text", "code", "tools"] },
  { id: "claude-3-5-haiku", name: "Claude 3.5 Haiku", provider: "anthropic", type: "chat", capabilities: ["text", "code"] },
  { id: "local-llama", name: "Local Llama", provider: "local", type: "chat", capabilities: ["text", "code"] },
];

const STREAMING_RESPONSES = [
  "I'll help you with that request. Let me analyze the best approach...",
  "Based on my analysis, here are the key considerations:",
  "To implement this, we should follow these steps:",
  "Here's a solution that addresses your requirements:",
];

export function PlaygroundView() {
  const { addLogEntry } = useDashboardStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showModelMenu, setShowModelMenu] = useState(false);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + "px";
    }
  };

  const handleSend = async () => {
    if ((!input.trim() && attachments.length === 0) || isStreaming) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input,
      timestamp: new Date().toISOString(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setAttachments([]);
    adjustTextareaHeight();

    await streamResponse(userMessage);
  };

  const streamResponse = async (userMessage: Message) => {
    setIsStreaming(true);
    abortControllerRef.current = new AbortController();

    try {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "",
        timestamp: new Date().toISOString(),
        isStreaming: true,
        metadata: {
          model: selectedModel.name,
        },
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Simulate streaming response
      const words = STREAMING_RESPONSES[Math.floor(Math.random() * STREAMING_RESPONSES.length)];
      let currentContent = "";
      
      for (let i = 0; i < words.length; i++) {
        if (abortControllerRef.current?.signal.aborted) break;
        
        currentContent += words[i];
        setMessages(prev => 
          prev.map(m => 
            m.id === assistantMessage.id 
              ? { ...m, content: currentContent } 
              : m
          )
        );
        
        await new Promise(resolve => setTimeout(resolve, 30));
        scrollToBottom();
      }

      // Add simulated code example
      if (selectedModel.capabilities.includes("code")) {
        const codeExample = "\n\n```typescript\n// Example implementation\nconst solution = {\n  approach: 'modern',\n  bestPractices: true\n};\n```\n";
        for (const char of codeExample) {
          if (abortControllerRef.current?.signal.aborted) break;
          
          currentContent += char;
          setMessages(prev => 
            prev.map(m => 
              m.id === assistantMessage.id 
                ? { ...m, content: currentContent } 
                : m
            )
          );
          
          await new Promise(resolve => setTimeout(resolve, 5));
          scrollToBottom();
        }
      }

      // Finalize message
      setMessages(prev => 
        prev.map(m => 
          m.id === assistantMessage.id 
            ? { 
                ...m, 
                content: currentContent + "\n\nLet me know if you'd like me to elaborate on any part of this response!",
                isStreaming: false,
                metadata: { ...m.metadata, tokens: Math.floor(currentContent.length / 4), duration: Math.floor(Math.random() * 2000) + 500 }
              } 
            : m
        )
      );

      addLogEntry({
        category: "agi",
        action: "Query Executed",
        details: `AGI query using ${selectedModel.name}`,
        severity: "info",
        source: "playground",
      });

    } catch (error) {
      console.error("Failed to stream response:", error);
      
      setMessages(prev => 
        prev.map(m => 
          m.id && m.isStreaming 
            ? { ...m, content: m.content + "\n\n**Error:** Failed to generate response.", isStreaming: false }
            : m
        )
      );
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const stopGeneration = () => {
    abortControllerRef.current?.abort();
    setIsStreaming(false);
    
    setMessages(prev => 
      prev.map(m => 
        m.isStreaming 
          ? { ...m, isStreaming: false } 
          : m
      )
    );
  };

  const regenerateResponse = (messageId: string) => {
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex <= 0) return;

    const userMessage = messages[messageIndex - 1];
    
    // Remove the existing assistant message
    setMessages(prev => prev.slice(0, messageIndex));
    
    // Generate new response
    streamResponse(userMessage);
  };

  const deleteMessage = (messageId: string) => {
    setMessages(prev => prev.filter(m => m.id !== messageId));
  };

  const copyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const attachment: Attachment = {
          id: Date.now().toString() + Math.random(),
          type: file.type.startsWith("image/") ? "image" : "file",
          name: file.name,
          content: reader.result as string,
        };
        
        if (attachment.type === "image") {
          attachment.preview = reader.result as string;
        }
        
        setAttachments(prev => [...prev, attachment]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const clearConversation = () => {
    setMessages([]);
    addLogEntry({
      category: "agi",
      action: "Conversation Cleared",
      details: "Cleared playground conversation",
      severity: "info",
      source: "playground",
    });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-[hsl(var(--primary))]" />
          <h1 className="font-semibold">AGI Playground</h1>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Model Selector */}
          <div className="relative">
            <button
              onClick={() => setShowModelMenu(!showModelMenu)}
              className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
            >
              <Terminal className="h-4 w-4" />
              <span>{selectedModel.name}</span>
              {showModelMenu ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            
            <AnimatePresence>
              {showModelMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 top-full z-50 mt-2 w-72 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-2 shadow-lg"
                >
                  {MODELS.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => {
                        setSelectedModel(model);
                        setShowModelMenu(false);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors",
                        selectedModel.id === model.id
                          ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"
                          : "hover:bg-[hsl(var(--muted))]"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Terminal className="h-4 w-4" />
                        <span>{model.name}</span>
                      </div>
                      <span className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{model.provider}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={clearConversation}
            className="rounded-lg border border-[hsl(var(--border))] p-2 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
            title="Clear conversation"
          >
            <Trash2 className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="flex flex-col items-center"
            >
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary)/.6)]">
                <Sparkles className="h-10 w-10 text-white" />
              </div>
              <h2 className="mt-6 text-lg font-semibold">Welcome to AGI Playground</h2>
              <p className="mt-2 max-w-md text-sm text-[hsl(var(--muted-foreground))]">
                Interact with advanced AI models. Ask questions, generate code, or upload images for analysis.
              </p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {selectedModel.capabilities.map(cap => (
                  <span key={cap} className="rounded-full bg-[hsl(var(--muted))] px-3 py-1 text-xs font-medium capitalize">
                    {cap}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "flex gap-3 max-w-4xl",
                  message.role === "user" ? "ml-auto flex-row-reverse" : ""
                )}
              >
                {/* Avatar */}
                <div className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                  message.role === "user" 
                    ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" 
                    : "bg-gradient-to-br from-purple-500 to-pink-500 text-white"
                )}>
                  {message.role === "user" ? (
                    <span className="text-sm font-bold">U</span>
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </div>

                {/* Content */}
                <div className={cn(
                  "flex-1 rounded-2xl px-4 py-3",
                  message.role === "user"
                    ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                    : "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]"
                )}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="text-xs font-medium opacity-70">
                      {message.role === "user" ? "You" : message.metadata?.model || "AI"}
                    </span>
                    {message.role === "assistant" && (
                      <div className="flex items-center gap-1">
                        {message.isStreaming && (
                          <div className="flex items-center gap-1 text-xs">
                            <div className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "0ms" }} />
                            <div className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "150ms" }} />
                            <div className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {message.attachments && message.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {message.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className={cn(
                            "flex items-center gap-2 rounded-lg bg-black/10 px-3 py-2 text-sm",
                            message.role === "user" ? "bg-black/10" : "bg-black/5"
                          )}
                        >
                          {attachment.type === "image" ? (
                            <ImageIcon className="h-4 w-4" />
                          ) : (
                            <Paperclip className="h-4 w-4" />
                          )}
                          <span className="max-w-[200px] truncate">{attachment.name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>

                  {message.role === "assistant" && !message.isStreaming && (
                    <div className="mt-3 flex items-center gap-2 border-t border-[hsl(var(--border))]/30 pt-2">
                      {message.metadata?.tokens && (
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                          {message.metadata.tokens} tokens
                        </span>
                      )}
                      {message.metadata?.duration && (
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                          {message.metadata.duration}ms
                        </span>
                      )}
                      <div className="flex-1" />
                      <button
                        onClick={() => copyMessage(message.content)}
                        className="rounded p-1 hover:bg-black/10 transition-colors cursor-pointer"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => regenerateResponse(message.id)}
                        className="rounded p-1 hover:bg-black/10 transition-colors cursor-pointer"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center gap-2 rounded-lg bg-[hsl(var(--muted))] px-3 py-2 text-sm"
              >
                {attachment.type === "image" ? (
                  <ImageIcon className="h-4 w-4" />
                ) : (
                  <Paperclip className="h-4 w-4" />
                )}
                <span className="max-w-[150px] truncate">{attachment.name}</span>
                <button
                  onClick={() => removeAttachment(attachment.id)}
                  className="rounded p-0.5 hover:bg-black/10 transition-colors cursor-pointer"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input Area */}
        <div className="flex gap-2">
          <label className="flex items-center justify-center rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-2 cursor-pointer hover:bg-[hsl(var(--muted))] transition-colors">
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            {selectedModel.capabilities.includes("image") || selectedModel.capabilities.includes("tools") ? (
              <ImageIcon className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
            ) : (
              <Paperclip className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
            )}
          </label>

          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              adjustTextareaHeight();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={`Chat with ${selectedModel.name}... (Shift+Enter for new line)`}
            className="flex-1 resize-none rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] min-h-[52px] max-h-[200px]"
          />

          {isStreaming ? (
            <button
              onClick={stopGeneration}
              className="flex items-center justify-center rounded-lg bg-red-500/10 px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-500/20 transition-colors cursor-pointer"
            >
              <StopCircle className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!input.trim() && attachments.length === 0}
              className="flex items-center justify-center rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
            >
              <Send className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
